import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { ConfirmationModalService } from '../confirmation-modal/confirmation-modal.service';
import { LoggerService } from '../logger/logger.service';

/**
 * Service de gestion des mises a jour de l'application via le Service Worker.
 *
 * Ce service detecte les nouvelles versions de l'application et propose
 * a l'utilisateur de mettre a jour. La mise a jour est effectuee en
 * rechargeant la page apres activation de la nouvelle version.
 */
@Injectable({
    providedIn: 'root',
})
export class UpdateService {
    private readonly swUpdate = inject(SwUpdate);
    private readonly modalService = inject(ConfirmationModalService);
    private readonly logger = inject(LoggerService);

    /**
     * Initialise le listener de mises a jour.
     * A appeler une seule fois au demarrage de l'application.
     */
    initialize(): void {
        if (!this.swUpdate.isEnabled) {
            this.logger.debug('Service Worker non active');
            return;
        }

        // Ecoute des mises a jour disponibles
        this.swUpdate.versionUpdates
            .pipe(
                filter(
                    (event): event is VersionReadyEvent =>
                        event.type === 'VERSION_READY',
                ),
            )
            .subscribe((event) => {
                this.logger.info('Nouvelle version disponible');
                this.logger.debug(
                    'Version actuelle:',
                    event.currentVersion.hash,
                );
                this.logger.debug(
                    'Nouvelle version:',
                    event.latestVersion.hash,
                );
                this.promptUpdate();
            });

        // Verification initiale des mises a jour
        this.checkForUpdate();
    }

    /**
     * Verifie manuellement si une mise a jour est disponible.
     */
    async checkForUpdate(): Promise<void> {
        if (!this.swUpdate.isEnabled) return;

        try {
            const updateAvailable = await this.swUpdate.checkForUpdate();
            this.logger.debug(
                'Verification MAJ terminee, disponible:',
                updateAvailable,
            );
        } catch (err) {
            this.logger.error('Echec de la verification des MAJ:', err);
        }
    }

    /**
     * Affiche une modale demandant a l'utilisateur de mettre a jour.
     */
    private promptUpdate(): void {
        this.modalService.openUpdateModal().subscribe(async (confirmed) => {
            if (confirmed) {
                await this.activateUpdate();
            }
        });
    }

    /**
     * Active la mise a jour et recharge la page.
     */
    private async activateUpdate(): Promise<void> {
        try {
            await this.swUpdate.activateUpdate();
            document.location.reload();
        } catch (err) {
            this.logger.error("Echec de l'activation de la MAJ:", err);
        }
    }
}
